import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import _ from 'lodash';
import Icon from 'components/Icon';
import { isBrowserIE, noop } from 'helpers/GlobalHelper';
import { additionalPosterEnabled } from 'helpers/ConfigHelper';
import {
  COLLECTION_TYPE_AFFILIATE_CATEGORY_LIST,
  filterMissingDeepLinkItems,
  nonFoxNowSkus,
  PANEL_TYPE_PARTNER_SHOWS,
  PANEL_TYPE_PARTNER_VIDEO,
} from 'helpers/ContentHelper';
import {
  Events,
  getSegmentModelMetadata,
  handleAnalytics,
  SegmentPageActionTypes,
  SegmentSources,
  SegmentUiElements,
} from '@foxcorp/delta-web-analytics';
import CarouselItem from './CarouselItem';
import CarouselPoster from './CarouselPoster';

import Style from './Carousel.module.scss';

const { MORE_EPISODES, SEARCH, WATCH } = SegmentPageActionTypes;
const { MORE_EPISODES: MORE_EPISODES_ELEMENT, PLAY, SERIES_TITLE } = SegmentUiElements;

class Carousel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      slideIndex: 0,
    };
    this.nextSlide = this.nextSlide.bind(this);
    this.previousSlide = this.previousSlide.bind(this);
    this.shouldShowArrows = this.shouldShowArrows.bind(this);
  }

  componentDidMount() {
    const { lastSlidePaginated } = this.props;
    if (lastSlidePaginated) {
      const doNotAnimate = true;
      this.slider.slickGoTo(lastSlidePaginated, doNotAnimate);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { breakpoint, favoritesManager, headline, items } = this.props;
    const { slideIndex } = this.state;
    const {
      breakpoint: newBreakpoint,
      favoritesManager: newFavoritesManager,
      headline: newHeadline,
      items: newItems,
    } = nextProps;

    const { slideIndex: newSlideIndex } = nextState;

    if (!_.isEqual(favoritesManager, newFavoritesManager)) return true;
    if (!_.isEqual(items, newItems)) return true;
    if (breakpoint && newBreakpoint && breakpoint !== newBreakpoint) return true;
    if (headline && newHeadline && headline !== newHeadline) return true;
    if (slideIndex !== newSlideIndex) return true;

    return false;
  }

  getPrevArrow() {
    const { slideIndex } = this.state;
    if (!this.shouldShowArrows() || slideIndex === 0) return null;
    const { roundArrows, carouselArrowOnClick } = this.props;
    const prevArrowIcon = roundArrows ? (
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-left" ariaHidden />
    ) : (
      <Icon color="white" icon="arrow-left" ariaHidden />
    );
    return (
      <button
        className="slick-arrow slick-prev"
        type="button"
        data-role="none"
        aria-label="show previous content"
        onClick={() => {
          this.previousSlide();
          carouselArrowOnClick();
        }}
      >
        {prevArrowIcon}
      </button>
    );
  }

  getNextArrow(itemsLength) {
    const { slideIndex } = this.state;
    const { roundArrows, carouselArrowOnClick, slidesToShow, onLastSlide } = this.props;
    const difference = itemsLength - slideIndex;

    if (!this.shouldShowArrows() || difference <= slidesToShow) {
      const lastSlideIndex = slideIndex;
      onLastSlide(lastSlideIndex);
      return null;
    }

    const nextArrowIcon = roundArrows ? (
      <Icon color="white" viewBox="0 0 43 44" icon="carousel-arrow-right" ariaHidden />
    ) : (
      <Icon color="white" icon="arrow-right" ariaHidden />
    );
    return (
      <button
        className="slick-arrow slick-next"
        type="button"
        aria-label="show next content"
        data-role="none"
        onClick={() => {
          this.nextSlide();
          carouselArrowOnClick();
        }}
      >
        {nextArrowIcon}
      </button>
    );
  }

  nextSlide() {
    this.slider.slickNext();
  }

  previousSlide() {
    this.slider.slickPrev();
  }

  shouldShowArrows() {
    const { items, showCarouselArrows, slidesToShow, useScrollableDiv, isPosters } = this.props;
    const itemsLength =
      isPosters && _.last(items).id === 'seeMore'
        ? _.get(items, 'length') - 1
        : _.get(items, 'length');
    const moreSlides = itemsLength > slidesToShow;
    const hideArrows = showCarouselArrows === false;
    const showArrows = useScrollableDiv || !items || hideArrows ? false : moreSlides;
    return showArrows;
  }

  seoHeaderWrapper = (seoElement, isFirst) => {
    if (isFirst) {
      return <h1>{seoElement}</h1>;
    }
    return seoElement;
  };

  renderItems() {
    const {
      addCarouselPlaceholders,
      applyInlineBackground,
      breakpoint,
      collectionCount,
      collectionName,
      playlistLength,
      collectionType,
      collectionVerticalPosition,
      descriptionLines,
      disableHoverStateActionButtons,
      favoritesManager,
      headline,
      isHoverStateV2,
      isPosters,
      isSeriesDetailAnonymous,
      isSeriesDetailPage,
      isSeasons,
      items,
      preventLazyLoad,
      noPreview,
      onTileClick,
      overrideAdditionalPosterStyle,
      overrideTheme,
      panel,
      panelCount,
      processor,
      posterType,
      renderImage,
      searchCompleted,
      setSearchCompleted,
      searchTerm,
      shouldDisplayNetworkLogo,
      shouldInvertTitleOrder,
      slidesToShow,
      subtitleLines,
      truncateSubtitle,
      theme,
      tileTheme,
      tileType,
      upcomingOverlay,
      isLiveTileV2,
    } = this.props;

    const { displayTemplate } = panel;
    const { slideIndex } = this.state;
    const itemsLength = items && items.length;
    if (!items || itemsLength === 0) return null;
    const { global_moreInfoButton: moreInfoText } = window.Language;
    const remainder = slideIndex % itemsLength;
    let itemToScaleFromLeft;
    let itemToScaleFromRight;
    if (remainder === 0) {
      itemToScaleFromRight = slidesToShow - 1;
    } else if (remainder >= slidesToShow) {
      const remainingItems = itemsLength - slideIndex;
      if (remainingItems > slidesToShow) {
        itemToScaleFromLeft = slideIndex;
        itemToScaleFromRight = slidesToShow + slideIndex - 1;
      } else {
        itemToScaleFromLeft = itemsLength - slidesToShow;
      }
    } else {
      itemToScaleFromLeft = itemsLength - slidesToShow;
      itemToScaleFromRight = itemsLength - 1;
    }
    let filteredItems = items;
    if (panel?.panelType === 'videos') {
      filteredItems = filterMissingDeepLinkItems(items, nonFoxNowSkus);
    }

    const carouselItems = filteredItems.map((item, idx) => {
      const customizedItem = {
        ...item,
        itemIndex: idx,
        slideIndex,
      };
      const { isSeriesDetailAvailable } = item;
      const processed = processor(customizedItem);
      if (processed.link === 'undefined') return null;
      const { description, link, isRedirect, overlayProps = {}, subtitle } = processed;

      const segmentPlaylistButtonAction = (props = {}) => {
        const { currentSeason, name, seriesName, showCode, uID } = item;
        const seasonNumber = currentSeason;
        let collectionTitle = null;
        if (headline) {
          collectionTitle =
            typeof headline === 'string'
              ? headline
              : _.get(headline, 'props.clipsSections[0].name');
        } else if (isSeriesDetailPage && isSeasons) {
          collectionTitle = `Season ${seasonNumber}`;
        }
        const itemSubtitle = _.isString(subtitle) ? subtitle : description;
        const seriesDetailVerticalPosition = isSeriesDetailPage && (isSeasons ? 1 : 2);
        const { actionType = '', uiElement = '' } = props;
        const actionLink =
          uiElement &&
          (uiElement === SERIES_TITLE || uiElement === MORE_EPISODES_ELEMENT) &&
          MORE_EPISODES;
        const actionSearch = !itemSubtitle && SEARCH;
        const pageActionType = actionSearch || actionLink || actionType || WATCH;
        const actionLocation = collectionName || SegmentSources.HOME;

        handleAnalytics({
          event: Events.PLAYLIST_BUTTON_ACTION,
          payload: {
            page_action_type: pageActionType,
            page_action_location: isSeriesDetailPage ? SegmentSources.DETAIL : actionLocation,
            page_collection_count: isSeriesDetailPage ? 2 : collectionCount,
            page_collection_id: panel?.id,
            page_collection_title: collectionTitle,
            page_item_horizontal_position: idx + 1,
            page_item_vertical_position: seriesDetailVerticalPosition || collectionVerticalPosition,
            page_item_title: seriesName || name,
            page_item_subtitle: itemSubtitle,
            page_playlist_length: playlistLength || _.get(this.props, 'items.length', 0),
            page_recs_collection: panel?.recommended,
            page_ui_element: uiElement || PLAY,
            page_video_uid: uID || showCode,
            ...getSegmentModelMetadata(),
          },
        });
      };

      const handleClick = (opts = {}) => {
        const { uiElement } = opts;
        const {
          title: titleText,
          description: fallbackSubtitle,
          subtitle: subtitleText,
          playButtonText,
          titleLink,
        } = overlayProps;
        const eventAction = isRedirect
          ? 'Exit Link'
          : playButtonText || (titleLink ? moreInfoText : undefined);
        onTileClick({
          item,
          idx,
          isRedirect,
          titleText,
          subtitleText: subtitleText || fallbackSubtitle,
          eventAction,
          link,
          uiElement,
        });

        segmentPlaylistButtonAction({ uiElement });
      };

      const onTileMouseDown = ({ uiElement }) => {
        segmentPlaylistButtonAction({ uiElement });
      };

      const key = `${item.id}-${idx}`;
      const itemProps = {
        applyInlineBackground,
        breakpoint,
        collectionType,
        disableHoverStateActionButtons,
        displayTemplate,
        favoritesManager,
        headline,
        isHoverStateV2,
        isLiveTileV2,
        isSeasons,
        isSeriesDetailAvailable,
        isSeriesDetailPage,
        item,
        itemIndex: idx,
        onTileClick: handleClick,
        onTileMouseDown,
        panelCount,
        playlist: panel?.playlist,
        preventLazyLoad,
        processed,
        renderImage,
        scaleFromLeft: idx === itemToScaleFromLeft,
        scaleFromRight: idx === itemToScaleFromRight,
        searchCompleted,
        searchTerm,
        segmentPlaylistButtonAction,
        setSearchCompleted,
        shouldDisplayNetworkLogo,
        shouldInvertTitleOrder,
        subtitleLines,
        tileTheme,
        tileType,
        truncateSubtitle,
        upcomingOverlay,
      };
      const posterProps = {
        breakpoint,
        descriptionLines,
        displayTemplate,
        favoritesManager,
        guid: item.guid,
        id: item.id,
        isHoverStateV2,
        isSeriesDetailAnonymous,
        item,
        itemIndex: idx,
        preventLazyLoad,
        posterType,
        maxItems: items.length,
        noPreview,
        overrideAdditionalPosterStyle,
        onPosterClick: handleClick,
        onTileClick: handleClick,
        overrideTheme,
        processed,
        renderImage,
        scaleFromLeft: idx === itemToScaleFromLeft,
        scaleFromRight: idx === itemToScaleFromRight,
        segmentPlaylistButtonAction,
        theme,
      };

      const { seriesRedirectMap } = window?.ExternalConfig || {};

      if (item?.seriesType === 'series' && seriesRedirectMap?.[item?.showCode]) {
        processed.link = seriesRedirectMap?.[item?.showCode];
      }

      if (item?.deeplinkWeb) {
        processed.link = item.deeplinkWeb;
      }

      if (isPosters) {
        const poster = (
          <div
            className={classNames(theme.carouselPosterContainer, Style.carouselPosterContainer)}
            key={key}
          >
            <CarouselPoster {...posterProps} />
          </div>
        );
        return item.id === 'seeMore' ? null : poster;
      }
      return (
        <div
          className={classNames(theme.carouselItemContainer, Style.carouselItemContainer)}
          key={key}
        >
          <CarouselItem {...itemProps} />
        </div>
      );
    });
    if (addCarouselPlaceholders && itemsLength < slidesToShow) {
      const placeholderCount = slidesToShow - itemsLength;
      for (let i = 0; i < placeholderCount; i += 1) {
        carouselItems.push(<div key={`placeholder-${i}`} className="carouselPlaceholder" />);
      }
    }
    return carouselItems;
  }

  renderHeader() {
    const {
      featuredTitleImage,
      headline,
      seeAll,
      isFirst,
      theme: defaultTheme,
      overrideTheme,
    } = this.props;
    const theme = overrideTheme || defaultTheme;
    let header = null;
    if (featuredTitleImage) {
      header = this.seoHeaderWrapper(
        <div className={theme.imageOnly}>
          <img className={theme.headlineImg} src={featuredTitleImage} alt={headline} />
        </div>,
        isFirst,
      );
    } else if (headline) {
      header = (
        <h2 aria-label={`${headline} heading`} className={theme.headline}>
          {headline}
        </h2>
      );
    } else {
      header = <div className={classNames(theme.emptyHeader, Style.emptyHeader)} />;
    }

    if (seeAll) {
      header = (
        <div className={theme.headlineWithSeeAll}>
          {header}
          {seeAll}
        </div>
      );
    }

    return header;
  }

  renderSliderDesktop() {
    const {
      autoplaySpeed,
      allowScrollLock,
      isInfinite,
      items,
      panel,
      responsive,
      slidesToShow,
      disableScroll,
      updateVisibleSlides,
      slidesToScroll,
    } = this.props;
    const { slideIndex } = this.state;

    let autoplaySettings = {};
    if (autoplaySpeed) {
      autoplaySettings = {
        autoplay: true,
        autoplaySpeed,
      };
    }
    const isIE = isBrowserIE();
    const useTransform = !isIE;
    const useCSS = !isIE;
    const itemsLength = _.get(items, 'length');
    const isHalfWay = Math.floor(itemsLength / 2) <= slideIndex;
    const settings = {
      afterChange: () => {
        if (allowScrollLock) {
          disableScroll({ disableScroll: false, bodyOnly: true });
        }
      },
      arrows: false,
      draggable: this.shouldShowArrows(),
      dots: false,
      beforeChange: (prevSlide, nextSlide) => {
        this.setState({ slideIndex: nextSlide });
        updateVisibleSlides(prevSlide, nextSlide, panel, isHalfWay);
      },
      infinite: isInfinite,
      slidesToScroll,
      responsive,
      slidesToShow,
      speed: 500,
      test: 'carousel-wrapper',
      ...autoplaySettings,
      useTransform,
      useCSS,
    };
    return (
      <>
        {this.renderHeader()}
        <Slider
          ref={s => {
            this.slider = s;
          }}
          {...settings}
          data-test="shared-carousel"
        >
          {this.renderItems()}
        </Slider>
      </>
    );
  }

  renderSliderMobile() {
    const { theme: defaultTheme, overrideTheme, collectionType } = this.props;
    const theme = overrideTheme || defaultTheme;
    const isCollectionCategoryList = collectionType === COLLECTION_TYPE_AFFILIATE_CATEGORY_LIST;
    return (
      <div>
        {this.renderHeader()}
        <div
          className={classNames([
            theme.scrollableDiv,
            isCollectionCategoryList && theme.categoryListDiv,
          ])}
        >
          {this.renderItems()}
        </div>
      </div>
    );
  }

  render() {
    const {
      isPosters,
      items,
      overrideAdditionalPosterStyle,
      panel,
      roundArrows,
      slidesToShow,
      theme,
      useScrollableDiv,
    } = this.props;
    const itemsLength = _.get(items, 'length');
    const isPartnerCollection =
      panel?.panelType === PANEL_TYPE_PARTNER_VIDEO ||
      panel?.panelType === PANEL_TYPE_PARTNER_SHOWS;
    const sliderContainer = [
      theme.container,
      Style.container,
      theme.redesignCollections,
      Style.redesignCollections,
    ];

    if (slidesToShow > itemsLength) {
      sliderContainer.push(theme.justifyLeft, Style.justifyLeft);
    }
    if (roundArrows) sliderContainer.push(theme.roundArrows, Style.roundArrows);
    const slider = useScrollableDiv ? this.renderSliderMobile() : this.renderSliderDesktop();
    return (
      <div
        className={classNames([
          theme.carouselContainer,
          Style.carouselContainer,
          isPosters && [
            theme.carouselPosterWrapper,
            Style.carouselPosterWrapper,
            isPartnerCollection && Style.partnerPosterWrapper,
          ],
          additionalPosterEnabled() && !overrideAdditionalPosterStyle && theme.additionalPoster,
        ])}
      >
        {this.getPrevArrow()}
        <div className={classNames(sliderContainer)}>{slider}</div>
        {this.getNextArrow(itemsLength)}
      </div>
    );
  }
}

Carousel.defaultProps = {
  addCarouselPlaceholders: false,
  allowScrollLock: false,
  applyInlineBackground: false,
  autoplaySpeed: null,
  breakpoint: null,
  carouselArrowOnClick: noop,
  collectionCount: null,
  collectionName: null,
  collectionType: null,
  collectionVerticalPosition: null,
  descriptionLines: 5,
  disableHoverStateActionButtons: false,
  disableScroll: noop,
  favoritesManager: null,
  featuredTitleImage: null,
  headline: null,
  isFirst: false,
  isHoverStateV2: false,
  isInfinite: true,
  isLiveTileV2: false,
  isPosters: false,
  isSeriesDetailAnonymous: false,
  isSeriesDetailPage: false,
  items: [],
  lastSlidePaginated: null,
  noPreview: false,
  onLastSlide: noop,
  onPosterClick: noop,
  onTileClick: noop,
  overrideAdditionalPosterStyle: false,
  overrideTheme: null,
  panel: {},
  panelCount: 0,
  playlistLength: null,
  preventLazyLoad: false,
  processor: item => item,
  posterType: '',
  renderImage: noop,
  responsive: [],
  roundArrows: false,
  searchCompleted: false,
  searchTerm: '',
  seeAll: null,
  segmentPlaylistButtonAction: noop,
  setSearchCompleted: noop,
  shouldDisplayNetworkLogo: true,
  shouldInvertTitleOrder: noop,
  showCarouselArrows: true,
  slidesToScroll: 1,
  slidesToShow: 3,
  theme: {},
  tileTheme: {},
  tileType: '',
  upcomingOverlay: noop,
  updateVisibleSlides: noop,
  useScrollableDiv: false,
};
Carousel.propTypes = {
  addCarouselPlaceholders: PropTypes.bool,
  allowScrollLock: PropTypes.bool,
  applyInlineBackground: PropTypes.bool,
  autoplaySpeed: PropTypes.number,
  breakpoint: PropTypes.string,
  carouselArrowOnClick: PropTypes.func,
  collectionCount: PropTypes.number,
  collectionName: PropTypes.string,
  collectionType: PropTypes.string,
  collectionVerticalPosition: PropTypes.number,
  descriptionLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disableHoverStateActionButtons: PropTypes.bool,
  disableScroll: PropTypes.func,
  favoritesManager: PropTypes.shape({}),
  featuredTitleImage: PropTypes.string,
  headline: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  isFirst: PropTypes.bool,
  isHoverStateV2: PropTypes.bool,
  isInfinite: PropTypes.bool,
  isLiveTileV2: PropTypes.bool,
  isPosters: PropTypes.bool,
  isSeasons: PropTypes.bool,
  isSeriesDetailAnonymous: PropTypes.bool,
  isSeriesDetailPage: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({})),
  lastSlidePaginated: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.number]),
  noPreview: PropTypes.bool,
  onLastSlide: PropTypes.func,
  onPosterClick: PropTypes.func,
  onTileClick: PropTypes.func,
  overrideAdditionalPosterStyle: PropTypes.bool,
  overrideTheme: PropTypes.shape({}),
  panel: PropTypes.shape({}),
  panelCount: PropTypes.number,
  playlistLength: PropTypes.number,
  preventLazyLoad: PropTypes.bool,
  processor: PropTypes.func,
  posterType: PropTypes.string,
  renderImage: PropTypes.func,
  responsive: PropTypes.arrayOf(PropTypes.shape({})),
  roundArrows: PropTypes.bool,
  searchCompleted: PropTypes.bool,
  searchTerm: PropTypes.string,
  seeAll: PropTypes.node,
  segmentPlaylistButtonAction: PropTypes.func,
  setSearchCompleted: PropTypes.func,
  shouldDisplayNetworkLogo: PropTypes.bool,
  shouldInvertTitleOrder: PropTypes.func,
  showCarouselArrows: PropTypes.bool,
  slidesToScroll: PropTypes.number,
  slidesToShow: PropTypes.number,
  subtitleLines: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.shape({}),
  tileTheme: PropTypes.shape(),
  tileType: PropTypes.string,
  truncateSubtitle: PropTypes.bool,
  upcomingOverlay: PropTypes.func,
  updateVisibleSlides: PropTypes.func,
  useScrollableDiv: PropTypes.bool,
};
export default Carousel;
